import React, { createContext, useContext } from 'react';
import useRetrieveData from 'global/hooks/useRetrieveData';
import useAccessRedirect from 'global/hooks/useAccessRedirect';
import { first, get } from 'global/lib/lodash';

// Library pages share one edition read so checklist content can wait on it.
// GET /editions/:id?include=realm is the access check (useAccessRedirect sends
// a failed or empty read back to /library). `ready` is true only when that
// read succeeded for this edition id — a previous edition's payload does not
// count after the id changes.
const EditionAccessContext = createContext(null);

const editionAccessGranted = (edition, editionId) => {
  if (!edition || edition.loading || edition.error || editionId == null || editionId === '') return false;
  const record = first(edition.data);
  return !!record && `${get(record, 'id')}` === `${editionId}`;
};

export function EditionAccessProvider({ editionId, skipRedirect = false, children }) {
  const edition = useRetrieveData(
    { url: `/editions/${editionId}` },
    {
      build: ['editions'],
      waitFor: !!editionId,
      params: { include: 'realm' },
      overrideNotFound: true,
      // Same window as other edition reads (minutes). A warm entry skips the network
      // and dedupes an in-flight request for this exact URL.
      useCache: { reload: 20 },
    }
  );

  // Add Existing passes skipRedirect so a 404 does not history.replace the overlay to /library.
  useAccessRedirect(edition, { skip: skipRedirect || !editionId });

  // No real id (PFM-2689 course rows use 0): there is nothing to wait on.
  const ready = !editionId || editionAccessGranted(edition, editionId);

  return (
    <EditionAccessContext.Provider value={{ edition, ready }}>
      {children}
    </EditionAccessContext.Provider>
  );
}

export function useEditionAccess() {
  return useContext(EditionAccessContext);
}

// Share-hash and course screens are outside the provider and stay ungated.
export function useEditionAccessReady() {
  const access = useEditionAccess();
  return access ? access.ready : true;
}
